// Hold-H cheat sheet (D34): every wired control, generated from the LIVE bindings so rebinds show
// correctly. ui/ touches only the DOM with injected data (T-ARCH-004) — no game internals.

import type { Bindings } from '../input/bindings';

const FRIENDLY: Record<string, string> = {
  Mouse0: 'LMB',
  Mouse1: 'MMB',
  Mouse2: 'RMB',
  Space: 'Space',
  ShiftLeft: 'Shift',
  ShiftRight: 'Shift',
};

function keyLabel(code: string): string {
  if (FRIENDLY[code]) return FRIENDLY[code]!;
  if (code.startsWith('Key')) return code.slice(3);
  if (code.startsWith('Digit')) return code.slice(5);
  return code;
}

/** Every wired control as [action, key] rows — the one source for the in-match sheet AND the lobby list.
 *  D137: the rows follow the controls-v2 taxonomy, and the context-split keys say what decides them. */
export function controlRows(b: Bindings): [string, string][] {
  const rows: [string, string][] = [
    ['Move', [b.moveUp, b.moveLeft, b.moveDown, b.moveRight].map(keyLabel).join(' ')], // D127: was the literal 'W A S D' — it lied the moment anyone rebound
    ['Look / aim', 'Mouse'],
    ['Sprint (hold)', keyLabel(b.sprint)],
    ['Shoot — hold to aim, release to fire', keyLabel(b.shoot)],
    ['Volley / header (press while the ball flies)', keyLabel(b.shoot)],
    ['Pass', keyLabel(b.passDirect)],
    ['Long pass', keyLabel(b.passLong)],
    [`High ball (hold + ${keyLabel(b.passDirect)}/${keyLabel(b.passLong)})`, keyLabel(b.loft)],
    ['Lob it up (tap) · higher (double tap)', keyLabel(b.loft)],
    ['Call for it — when a teammate has the ball', keyLabel(b.call)],
    ['Shield (hold) — while you have the ball', keyLabel(b.shield)],
    ['Tackle — safe, never a foul', keyLabel(b.tackle)],
    ['Hard tackle — while they have the ball', keyLabel(b.tackleHard)],
    ['Slide tackle', keyLabel(b.slide)],
    ['Block (hold) — while you do not have the ball', keyLabel(b.block)],
    ['Dive / go down', keyLabel(b.diveFlop)],
    ['Punch (ref watches!)', keyLabel(b.punch)],
    ['Keeper dive — only when facing a penalty', `${keyLabel(b.gkDiveL)} / ${keyLabel(b.gkDiveR)}`],
    ['Switch player', keyLabel(b.switchPlayer)], // D127: was a literal
    ['Save debug log (+ clip with ?clip)', 'F8'], // D56/D60
    ['This cheat sheet', `${keyLabel(b.cheatSheet)} (hold)`], // D127: was a literal
    ['Pause / release mouse', 'ESC'],
  ];
  if (b.shove !== '') rows.splice(rows.length - 4, 0, ['Shoulder barge', keyLabel(b.shove)]); // PT4-P14: unbound by default
  return rows;
}

/** D39: compact two-column controls block for the LOBBY/menu card (always visible — no hidden hotkey). */
export function controlsBlock(doc: Document, bindings: Bindings): HTMLElement {
  const wrap = doc.createElement('div');
  wrap.style.cssText = 'margin-top:16px;padding-top:10px;border-top:1px solid rgba(140,220,160,.25)';
  const t = doc.createElement('div');
  t.style.cssText = 'font-size:12px;font-weight:700;letter-spacing:1px;opacity:.8;margin-bottom:6px';
  t.textContent = '⌨ CONTROLS';
  wrap.appendChild(t);
  const grid = doc.createElement('div');
  grid.style.cssText = 'display:grid;grid-template-columns:1fr 1fr;gap:1px 22px';
  for (const [action, key] of controlRows(bindings)) {
    const row = doc.createElement('div');
    row.style.cssText = 'display:flex;justify-content:space-between;gap:10px;font-size:11px;opacity:.85';
    const a = doc.createElement('span');
    a.textContent = action;
    const k = doc.createElement('span');
    k.style.cssText = 'font-weight:700;color:#9fc';
    k.textContent = key;
    row.append(a, k);
    grid.appendChild(row);
  }
  wrap.appendChild(grid);
  return wrap;
}

export class CheatSheet {
  private readonly el: HTMLDivElement;

  constructor(doc: Document, bindings: Bindings) {
    const rows = controlRows(bindings);
    this.el = doc.createElement('div');
    this.el.style.cssText =
      'position:fixed;inset:0;display:none;align-items:center;justify-content:center;background:rgba(4,22,12,.88);z-index:115;font-family:ui-monospace,monospace;color:#e8ffe8';
    const panel = doc.createElement('div');
    panel.style.cssText = 'padding:20px 30px;border:1px solid #274;border-radius:12px;background:#08160e;min-width:300px';
    const title = doc.createElement('div');
    title.style.cssText = 'font-size:18px;font-weight:700;margin-bottom:12px;letter-spacing:1px';
    title.textContent = '⌨ CONTROLS';
    panel.appendChild(title);
    for (const [action, key] of rows) {
      const row = doc.createElement('div');
      row.style.cssText = 'display:flex;justify-content:space-between;gap:24px;padding:2px 0;font-size:13px';
      const a = doc.createElement('span');
      a.style.opacity = '0.85';
      a.textContent = action;
      const k = doc.createElement('span');
      k.style.cssText = 'font-weight:700;color:#9fc';
      k.textContent = key;
      row.append(a, k);
      panel.appendChild(row);
    }
    this.el.appendChild(panel);
    doc.body.appendChild(this.el);
  }

  show(): void {
    this.el.style.display = 'flex';
  }
  hide(): void {
    this.el.style.display = 'none';
  }
  get visible(): boolean {
    return this.el.style.display !== 'none';
  }
}
